import React from 'react';
import List from '@src/components/List/List';
import {IListConfig} from '@src/@interfaces/components/List/IListConfig';
import RenderIf from '@src/components/RenderIf/RenderIf';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import Translate from '@src/components/Translate/Translate';
import {UserContext} from '@src/contexts/UserContext';
import ModelView from './ModelView';
import {IReduxModelConfigsState} from '@src/@interfaces';
import {ModelViewProps} from '@src/@types/components/ModelView';
import app from '@src/helpers/app';
import {ListProps} from '@src/@types/components/List';
import PageTitle from '@src/components/PageMetaTags/PageTitle';
import trans from '@src/helpers/trans';
import config from '@src/helpers/config';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';

const emptyObject = {};

export type ListViewProps<Model extends IEmptyModel> = ModelViewProps<Model> & {
    uniqueListName?: string,
} & Pick<
        ListProps<Model>,
        'onRowClicked'
        | 'chosenFilterId'
        | 'chosenFilterName'
        | 'dontChangeUserPreferences'
        | 'forceColumnsValues'
        | 'disabledColumns'
        | 'forceDisplayFilters'
        | 'defaultDisplayFilters'
>;

export class ListView<
        Model extends IEmptyModel = IEmptyModel,
        Props extends Record<any, any> = Record<any, any>,
> extends ModelView<ListViewProps<Model> & Props, IListConfig> {
    static configsContainerName: keyof Pick<IReduxModelConfigsState, 'listConfigs' | 'formConfigs'> = 'listConfigs';

    protected renderUnconnectedToStoreException(message: string, exampleOfConnection: string | null = null) {
        exampleOfConnection ??= `ListView.getStoreConnector()(ListView)`;

        return super.renderUnconnectedToStoreException(message, exampleOfConnection);
    }

    protected renderContent(): React.ReactNode {
        return <UserContext.Consumer>{userContext => {
            const permissions = this.getAccessPermissions();
            const accessDenied = !userContext?.canAny(permissions);

            return this.renderCard(accessDenied);
        }}</UserContext.Consumer>;
    }

    protected renderCard(accessDenied: boolean) {
        const errorHasOccurred = !this.getConfig() && !this.nowLoading();
        if (errorHasOccurred) {
            return <>
                <PageTitle title={trans('Exceptions.not_found') + ' | ' + config('client.app-name')}/>
                <Translate
                        message="Exceptions.configuration.model-form-configuration-file-not-found"
                        className="alert alert-danger py-5 text-center"
                        tag="div"
                />
            </>;
        }

        return this.renderCardContent(accessDenied);
    }

    protected renderCardContent(accessDenied: boolean) {
        if (accessDenied) {
            return this.renderAccessDeniedError();
        }

        return <>
            {this.getConfigError() ? <div className="mb-3">{this.renderErrorRepresentation()}</div> : null}

            <RenderIf expression={this.nowLoading()}>
                <div className="card card-body">
                    <div className="py-5 w-100 d-flex align-items-center justify-content-center text-muted">
                        <LoadingIndicator type="chase" className="me-3"/>
                        <Translate message="@common-phrases.loading"/>
                    </div>
                </div>
            </RenderIf>

            <RenderIf expression={this.getConfig()}>{config => {
                return <List<Model>
                        {...this.getPropsForListComponent()}
                        uniqueListName={
                                this.props.uniqueListName ??
                                app('names-resolver').listNames.getUniqueName(this.props.controllerName)
                        }
                        config={config}
                        listConfigContextOptions={this.getListConfigContextOptions()}
                        tableProps={this.getPropsForTableComponent()}
                        tableHeadProps={this.getPropsForTableHeadComponent()}
                        tableBodyProps={this.getPropsForTableBodyComponent()}
                        tableCaptionNode={this.getTableCaptionNode()}
                        renderComponentBeforeTheTopPagination={this.getComponentToRenderBeforeTheTopPagination()}
                        renderComponentAfterTheTopPagination={this.getComponentToRenderAfterTheTopPagination()}
                        renderComponentBeforeTheBottomPagination={this.getComponentToRenderBeforeTheBottomPagination()}
                        renderComponentAfterTheBottomPagination={this.getComponentToRenderAfterTheBottomPagination()}
                        onRowClicked={this.props.onRowClicked}
                        controllerName={this.props.controllerName}
                        chosenFilterId={this.props.chosenFilterId}
                        chosenFilterName={this.props.chosenFilterName}
                        dontChangeUserPreferences={this.props.dontChangeUserPreferences}
                        forceColumnsValues={this.props.forceColumnsValues}
                        disabledColumns={this.props.disabledColumns}
                        isIsolatedComponent={this.props.isIsolatedComponent}
                        forceDisplayFilters={this.props.forceDisplayFilters}
                        defaultDisplayFilters={this.props.defaultDisplayFilters}
                />;
            }}</RenderIf>
        </>;
    }

    protected getPropsForListComponent(): Partial<NonNullable<ListViewProps<Model>['listProps']>> {
        return this.props.listProps ?? {};
    }

    protected getPropsForTableComponent(): ListProps<Model>['tableProps'] {
        return undefined;
    }

    protected getPropsForTableHeadComponent(): ListProps<Model>['tableHeadProps'] {
        return undefined;
    }

    protected getPropsForTableBodyComponent(): ListProps<Model>['tableBodyProps'] {
        return undefined;
    }

    protected getTableCaptionNode(): ListProps<Model>['tableCaptionNode'] {
        return undefined;
    }

    protected getComponentToRenderBeforeTheTopPagination(): ListProps<Model>['renderComponentBeforeTheTopPagination'] {
        return undefined;
    }

    protected getComponentToRenderAfterTheTopPagination(): ListProps<Model>['renderComponentAfterTheTopPagination'] {
        return undefined;
    }

    protected getComponentToRenderBeforeTheBottomPagination(): ListProps<Model>['renderComponentBeforeTheBottomPagination'] {
        return undefined;
    }

    protected getComponentToRenderAfterTheBottomPagination(): ListProps<Model>['renderComponentAfterTheBottomPagination'] {
        return undefined;
    }

    protected getListConfigContextOptions(): ListProps<Model>['listConfigContextOptions'] {
        return emptyObject;
    }

    protected getAccessPermissions(): string | string[] {
        const config = this.getConfig();
        if (config && config.listPermissions) {
            return config.listPermissions;
        }

        return app('names-resolver').permissions.getIndexRoutePermissionName(this.props.controllerName);
    }

    static getPageTitleByControllerName(controllerName: string): string | null {
        const config = ListView.tryToGetConfigByControllerName<IListConfig>(controllerName);
        if (!config || !config.title) {
            return null;
        }

        return config.title;
    }
}

export default ListView.getStoreConnector()(ListView);
